// t7-3 (A1 y A3, #557 y #558): LAS CARDS DEL PROFE EN LA BODEGA DEL TELEFONO.
//
// "Para el estudiante es como si fuera un doc mas que el haya subido" (Carlos, 29-sep).
// En el telefono una card vive como un DOCUMENTO de la bodega con id `pub:<publicacion_id>`
// y sus preguntas con id `pub:<publicacion_id>:<question_id>` (las MISMAS llaves que arma el
// servidor en `backend/services/pipe_inst/paquete_card.py`). El prefijo hace que una card no
// choque nunca con un documento propio, y que dos cards del mismo documento del profe no se
// pisen en la tabla `questions`.
//
// Este modulo es PURO salvo las dos funciones de la foto de los grupos (la ultima lista de
// grupos que llego CON RED, en `sync_meta`, hermana de `docs_snapshot`). No pide nada a la red.
//
// LA LISTA MANDA. El cartero solo mueve cards cuando HAY una foto de los grupos: sin foto
// (primera vez, invitado, web) no baja, no bloquea y no borra ninguna. Con foto:
//   - card ACTIVA sin paquete, o con `material_rev` distinto: se baja;
//   - card CADUCADA (dice el servidor, o sin red su `caduca` contra el reloj del telefono):
//     nunca se baja; si ya estaba, se conserva BLOQUEADA;
//   - card que ya no viene en la lista: se borra (convergencia), igual que un documento.
// La conexion nativa se pide AL USARLA (import diferido): este modulo lo importan el Panel y la
// tarjeta de la card, y en la web no debe arrastrar el puente de SQLite.
import { SYNC_META_KEYS } from './schema';

export const PREFIJO_CARD = 'pub:';

/** `pub:<id>`: el id del documento de una card en la bodega. */
export function idLocalDeCard(publicacionId) {
  return `${PREFIJO_CARD}${publicacionId}`;
}

/** Cierto si el id es el de una card en la bodega (documento o pregunta). */
export function esIdDeCard(id) {
  return typeof id === 'string' && id.startsWith(PREFIJO_CARD);
}

/** El `publicacion_id` de un id local de documento (`pub:<id>`), o null. */
export function cardDeIdLocal(id) {
  if (!esIdDeCard(id)) return null;
  const resto = id.slice(PREFIJO_CARD.length);
  return resto && !resto.includes(':') ? resto : null;
}

/** Todas las cards de la lista de grupos, en el orden del servidor. */
export function cardsDeLosGrupos(grupos) {
  const salida = [];
  for (const g of Array.isArray(grupos) ? grupos : []) {
    for (const c of Array.isArray(g && g.cards) ? g.cards : []) {
      if (c && c.id) salida.push(c);
    }
  }
  return salida;
}

/**
 * Caducada. Con red manda el servidor (`estado`); sin red, ademas, la fecha `caduca` que ya
 * traia la lista contra el reloj del telefono (#558).
 */
export function cardCaducada(card, { sinRed = false, ahora = Date.now() } = {}) {
  if (!card) return true;
  if (card.estado === 'caducada') return true;
  if (!sinRed || !card.caduca) return false;
  const limite = Date.parse(card.caduca);
  return Number.isFinite(limite) && ahora >= limite;
}

/** La lista con el estado de cada card recalculado sin red (la fecha contra el reloj). */
export function gruposSinRed(grupos, ahora = Date.now()) {
  if (!Array.isArray(grupos)) return grupos;
  return grupos.map((g) => {
    const cards = Array.isArray(g && g.cards) ? g.cards : [];
    if (!cards.some((c) => c && c.estado !== 'caducada' && cardCaducada(c, { sinRed: true, ahora }))) return g;
    const activas = [];
    const caducadas = [];
    for (const c of cards) {
      if (c && c.estado !== 'caducada' && cardCaducada(c, { sinRed: true, ahora })) {
        caducadas.push({ ...c, estado: 'caducada' });
      } else if (c && c.estado === 'caducada') {
        caducadas.push(c);
      } else {
        activas.push(c);
      }
    }
    // Las caducadas van hasta abajo (la regla de la lista del servidor).
    return { ...g, cards: activas.concat(caducadas) };
  });
}

function revDe(valor) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/**
 * El plan del cartero para las cards (puro).
 * @param {Array|null} grupos  la foto de los grupos (null = no hay foto: no se mueve nada)
 * @param {Array} filasLocales  filas `documents` de la bodega ({id, material_rev, locked_at})
 * @returns {{bajar: Array<{card, id, local}>, bloquear: string[], desbloquear: string[], borrar: string[]}}
 */
export function planDeCards(grupos, filasLocales, { sinRed = false, ahora = Date.now() } = {}) {
  const plan = { bajar: [], bloquear: [], desbloquear: [], borrar: [] };
  if (!Array.isArray(grupos)) return plan;
  const locales = new Map();
  for (const r of Array.isArray(filasLocales) ? filasLocales : []) {
    if (r && esIdDeCard(r.id)) locales.set(r.id, r);
  }
  const enLaLista = new Set();
  for (const card of cardsDeLosGrupos(grupos)) {
    const id = idLocalDeCard(card.id);
    enLaLista.add(id);
    const local = locales.get(id);
    if (cardCaducada(card, { sinRed, ahora })) {
      if (local && local.locked_at == null) plan.bloquear.push(id);
      continue;
    }
    if (local && local.locked_at != null) plan.desbloquear.push(id);
    if (!local || revDe(local.material_rev) !== revDe(card.material_rev)) {
      plan.bajar.push({ card, id, local: local || null });
    }
  }
  for (const id of locales.keys()) {
    if (!enLaLista.has(id)) plan.borrar.push(id);
  }
  return plan;
}

/**
 * Los huecos de cards para la convergencia y el latido (puro): las cards ACTIVAS cuyo paquete
 * no esta completo en el telefono. `presencia` es la de `readLocalPresence`.
 */
export function huecosDeCards(grupos, presencia, { sinRed = false, ahora = Date.now() } = {}) {
  if (!Array.isArray(grupos)) return [];
  const porId = new Map((Array.isArray(presencia) ? presencia : []).map((r) => [r.id, r]));
  const huecos = [];
  for (const card of cardsDeLosGrupos(grupos)) {
    if (cardCaducada(card, { sinRed, ahora })) continue;
    const id = idLocalDeCard(card.id);
    const local = porId.get(id);
    if (!local || !(local.local_question_rows > 0) || revDe(local.material_rev) !== revDe(card.material_rev)) {
      huecos.push(id);
    }
  }
  return huecos;
}

/** La huella de lo que el cartero mira de la lista: cambia si entra, sale o cambia una card. */
export function huellaDeCards(grupos) {
  if (!Array.isArray(grupos)) return '';
  return cardsDeLosGrupos(grupos)
    .map((c) => `${c.id}:${c.estado || ''}:${revDe(c.material_rev)}:${c.caduca || ''}`)
    .join('|');
}

// ── LA FOTO DE LOS GRUPOS (sync_meta) ─────────────────────────────────────────

/** Guarda la ultima lista de grupos que llego CON RED. Nunca lanza. */
export async function guardarGruposEnBodega(grupos) {
  if (!Array.isArray(grupos)) return;
  try {
    const { isNativePlatform, openWarehouse } = await import('./connection');
    if (!isNativePlatform()) return;
    const api = await openWarehouse();
    await api.run(
      'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
      [SYNC_META_KEYS.GRUPOS_SNAPSHOT, JSON.stringify(grupos)],
      false
    );
  } catch (e) { /* la foto es una mejora de entrega; sin ella la app sigue como hoy */ }
}

/** La ultima lista de grupos guardada, o null (no hay foto). Nunca lanza. */
export async function leerGruposDeBodega(api = null) {
  try {
    const { isNativePlatform, openWarehouse } = await import('./connection');
    if (!isNativePlatform()) return null;
    const wh = api || (await openWarehouse());
    const rows = await wh.query('SELECT value FROM sync_meta WHERE key = ?;', [SYNC_META_KEYS.GRUPOS_SNAPSHOT]);
    const crudo = rows.length > 0 ? rows[0].value : null;
    if (!crudo) return null;
    const grupos = JSON.parse(crudo);
    return Array.isArray(grupos) ? grupos : null;
  } catch (e) {
    return null;
  }
}

// ── EL EXAMEN DE CARDS EN LA FOTO DE /sync/state (#559) ───────────────────────

/**
 * Devuelve a las llaves LOCALES los examenes de la foto que traen cards: el servidor los guarda
 * con el documento REAL del profe y las preguntas reales; el telefono los reconoce por
 * `pub:<card>` (asi A+B solo reanuda A+B, y dos cards del mismo documento no se confunden).
 * Una pregunta va a la primera card del examen que la tiene en la bodega (la de un documento
 * propio, o la de una card que aun no baja, conserva su id). Sin ningun examen con
 * cards devuelve el MISMO arreglo (la foto de hoy, sin tocar).
 * @param {Array} examenes  `state.exam_sessions`
 * @param {Set<string>} propios  ids de los documentos propios (los de `state.documents`)
 * @param {Set<string>} preguntasLocales  ids `pub:<card>:<pregunta>` que hay en la bodega
 */
export function examenesConLlavesLocales(examenes, propios, preguntasLocales) {
  if (!Array.isArray(examenes)) return examenes;
  if (!examenes.some((ex) => ex && Array.isArray(ex.publicacion_ids) && ex.publicacion_ids.length > 0)) {
    return examenes;
  }
  return examenes.map((ex) => {
    const pids = ex && Array.isArray(ex.publicacion_ids) ? ex.publicacion_ids : [];
    if (pids.length === 0) return ex;
    // Una pregunta de un documento PROPIO (examen mixto) no esta en ningun paquete de card: se
    // queda con su id.
    const local = (qid) => {
      for (const pid of pids) {
        const candidato = `${PREFIJO_CARD}${pid}:${qid}`;
        if (preguntasLocales.has(candidato)) return candidato;
      }
      return qid;
    };
    const llaves = (obj) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
      const out = {};
      for (const [k, v] of Object.entries(obj)) out[local(k)] = v;
      return out;
    };
    const salida = {
      ...ex,
      document_ids: (ex.document_ids || []).filter((d) => propios.has(d))
        .concat(pids.map((p) => idLocalDeCard(p))),
      question_ids: (ex.question_ids || []).map(local),
    };
    for (const campo of ['answers_so_far', 'question_times', 'rng_seeds']) {
      if (ex[campo] !== undefined) salida[campo] = llaves(ex[campo]);
    }
    return salida;
  });
}
